import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  DepartmentItem,
  DepartmentListItem,
  GetDepartmentsListPaginatedResponse,
} from "../../../Interfaces/DepartmentInterface";
import type { DepartmentsListState } from "../../../Interfaces/ReduxSliceInterface";
import type { FacilityItem } from "../../../Interfaces/FacilityInterface";
import { getFacilityLabel } from "../../../utils/facilityDisplayName";

/** Payload for setDepartmentsList - API response + facilityId + optional facilities to resolve unit name */
interface SetDepartmentsListPayload {
  response: GetDepartmentsListPaginatedResponse;
  facilityId: string;
  /** From GET /facilities (get all units); used to show unit name when filter is applied */
  facilities?: FacilityItem[];
}

function mapDepartmentItemToListItem(
  d: DepartmentItem,
  facilityName?: string
): DepartmentListItem {
  const item: DepartmentListItem = {
    _id: d._id,
    name: d.name,
    type: "Doctor",
    code: d.code,
    createdAt: d.createdAt,
  };
  if (facilityName) {
    item.units = [facilityName];
  }
  return item;
}

export type { DepartmentListItem };

const initialState: DepartmentsListState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  limit: 20,
  facilityId: null,
};

const departmentsListSlice = createSlice({
  name: "departmentsList",
  initialState,
  reducers: {
    setDepartmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    setDepartmentsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.items = [];
        state.total = 0;
      }
    },
    /** Updates departments list from API response. Call API in component, dispatch this with result. */
    setDepartmentsList: (state, action: PayloadAction<SetDepartmentsListPayload>) => {
      const { response, facilityId, facilities } = action.payload;
      const facility = facilityId && facilities?.length
        ? facilities.find((f) => f.id === facilityId)
        : undefined;
      const facilityName = facility ? getFacilityLabel(facility) : undefined;
      const items = (response.items ?? []).map((d) =>
        mapDepartmentItemToListItem(d, facilityName)
      );
      state.items = items;
      state.total = response.total ?? items.length;
      state.page = 1;
      state.limit = 20;
      state.facilityId = facilityId;
      state.loading = false;
      state.error = null;
    },
    clearDepartmentsList: (state) => {
      state.items = [];
      state.error = null;
      state.total = 0;
      state.page = 1;
      state.facilityId = null;
    },
    setDepartmentsPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    addDepartment: (state, action: PayloadAction<DepartmentListItem>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    /** Update a single department in the list by _id */
    updateDepartment: (state, action: PayloadAction<{ _id: string; name?: string; code?: string; type?: string }>) => {
      const { _id, name, code, type } = action.payload;
      const item = state.items.find((i) => i._id === _id);
      if (item) {
        if (name !== undefined) item.name = name;
        if (code !== undefined) item.code = code;
        if (type !== undefined) item.type = type;
      }
    },
  },
});

export const {
  setDepartmentsLoading,
  setDepartmentsError,
  setDepartmentsList,
  clearDepartmentsList,
  setDepartmentsPage,
  addDepartment,
  updateDepartment,
} = departmentsListSlice.actions;

export default departmentsListSlice.reducer;
